var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/x-buildify-toolbar.ts
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-button.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/elements/components/x-grid.js";
import "@adornis/chemistry/elements/components/x-icon.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import "@adornis/dragdrop/x-drag-drop.js";
import "@adornis/fonts/fonts.js";
import "@adornis/popover/x-tooltip.js";
import { html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { repeat } from "lit/directives/repeat.js";
import "./components/x-buildify-size-picker.js";
import "./components/x-icon-button.js";
import {
  AutoScrollContainerConsumer,
  ClipboardConsumer,
  ContentConsumer,
  CustomContentConsumer,
  ExtensionsConsumer,
  IdentifierConsumer,
  ModeConsumer,
  OptionsConsumer,
  SizeBreakpointConsumer,
  SizeConsumer
} from "./globals/consumer.js";
import { Contexts, Droppable, Event } from "./globals/enums.js";
import { ThrowEventDraggingChanged } from "./globals/events.js";
import "./x-buildify-visualization.js";
var CUSTOM_CONTENT_GROUP_NAME = "Benutzerdefiniert";
var XBuildifyToolbar = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this._consumedExtensions = ExtensionsConsumer(this);
    this._consumedIdentifier = IdentifierConsumer(this, false);
    this._consumedClipboard = ClipboardConsumer(this);
    this._consumedMode = ModeConsumer(this);
    this._consumedOptions = OptionsConsumer(this);
    this._consumdData = ContentConsumer(this);
    this._consumedBreakPoints = SizeBreakpointConsumer(this);
    this._consumedSize = SizeConsumer(this);
    this._consumedAutoScrollContainer = AutoScrollContainerConsumer(this);
    this._consumedCustomContent = CustomContentConsumer(this);
    this._isMinimized = false;
    this._no_group_symbol = "NO_GROUP";
  }
  render() {
    const extensions = this._consumedExtensions.value;
    const identifier = this._consumedIdentifier.value;
    const clipboard = this._consumedClipboard.value;
    if (!extensions || !identifier)
      return nothing;
    return xComponents(html`
      ${this.__items({ extensions, identifier, clipboard })}
      ${this._consumedMode.value === "edit" ? this._spacer() : nothing}

      <x-flex horizontal space="sm" wrap ${css({ flex: "1" })}
        >${this._sizePicker()} ${this._spacer()} ${this._options()}
      </x-flex>
    `);
  }
  _groupExtensions(extensions) {
    return extensions.reduce((acc, curr) => {
      (acc[curr.group || this._no_group_symbol] ??= []).push(curr);
      return acc;
    }, {});
  }
  __items({
    extensions,
    identifier,
    clipboard
  }) {
    if (this._consumedMode.value === "view")
      return html` <div ${css({ flex: "3" })}></div> `;
    let extensionsToVisualize = extensions;
    const groupedExtensions = this._groupExtensions(extensions.filter((e) => !!e.toolbar));
    if (this._consumedCustomContent.value)
      groupedExtensions[CUSTOM_CONTENT_GROUP_NAME] = [];
    extensionsToVisualize = groupedExtensions[this._selectedGroup ?? this._no_group_symbol] ?? [];
    const showSecondLayer = this._selectedGroup === CUSTOM_CONTENT_GROUP_NAME ? this._consumedCustomContent.value : extensionsToVisualize.length;
    return xComponents(html`
      <x-flex space="sm" ${css({ flex: "3" })} crossaxis-start center>
        ${Object.keys(groupedExtensions).length > 1 ? html`
                <x-flex horizontal wrap space="xs" ?flex=${!this._selectedGroup}>
                  ${repeat(Object.keys(groupedExtensions), (group) => this.__group(group, this._selectedGroup === group))}
                  <!-- clipboard -->
                  ${this.__clipboard(clipboard, identifier)}
                </x-flex>
              ` : nothing}
        ${showSecondLayer ? html`
                ${this.dragDropWrapper(
      this._selectedGroup === CUSTOM_CONTENT_GROUP_NAME ? this.__customContent() : repeat(
        extensionsToVisualize,
        (extension) => extension.class._class,
        (extension) => this.__item(
          identifier,
          Droppable.EXTENSION,
          extension,
          extension.toolbar.icon,
          extension.toolbar.text.default
        )
      )
    )}
              ` : nothing}
      </x-flex>
      ${showSecondLayer ? html`
              <x-flex crossaxis-center>
                <x-icon-button
                  background="transparent"
                  icon=${this.configuration.switchMinimize.icon}
                  @click=${() => this._isMinimized = !this._isMinimized}
                >
                </x-icon-button>
              </x-flex>
            ` : nothing}
      </x-flex>
    `);
  }
  __clipboard(clipboard, identifier) {
    if (!clipboard)
      return nothing;
    return html`
      ${this._spacer()}
      ${this.dragDropWrapper(
      this.__item(identifier, Droppable.CLIPBOARD, Contexts.CLIPBOARD, "content_copy", "Zwischenablage")
    )}
    `;
  }
  __customContent() {
    if (this._selectedGroup !== CUSTOM_CONTENT_GROUP_NAME)
      return nothing;
    const customContent = this._consumedCustomContent.value;
    if (!customContent)
      return nothing;
    return customContent.renderToolbarItems(this.__item);
  }
  __group(group, selected) {
    return html`
      <x-icon-button
        icon=${selected ? this.configuration.groups.folderOpenIcon : this.configuration.groups.folderClosedIcon}
        ${css({ userSelect: "none", minWidth: "80px", maxWidth: "150px" })}
        text=${group}
        background=${ifDefined(selected ? this.colors.neutral.shade(800) : void 0)}
        color=${ifDefined(selected ? this.colors.on.neutral.shade(800) : void 0)}
        @click=${() => this._selectedGroup = this._selectedGroup === group ? void 0 : group}
      >
      </x-icon-button>
    `;
  }
  __item(identifier, type, value, icon, text, clickAction) {
    return xComponents(html`
      <x-icon-button
        ${css({ userSelect: "none" })}
        @click=${() => clickAction?.()}
        identifier=${identifier}
        .type=${type}
        .value=${value}
        class="bar-item"
        icon=${icon}
        text=${ifDefined(this._isMinimized ? void 0 : text)}
      >
        ${this._isMinimized ? html`
              <x-tooltip>
                <x-text> ${text} </x-text>
              </x-tooltip>
            ` : nothing}
      </x-icon-button>
    `);
  }
  _spacer() {
    return html`
      <div flex-none ${css({ width: "1px", background: this.colors.neutral.shade(200), alignSelf: "stretch" })}></div>
    `;
  }
  _options() {
    return html`
      <x-flex horizontal space="sm">
        <x-icon-button
          mode="icon"
          icon=${this.configuration.preview.icon}
          @click=${() => {
      this.dispatchEvent(new CustomEvent("switch-mode", { composed: true }));
    }}
        >
          <x-tooltip .text=${xComponents(html` <x-text> ${this.configuration.preview.tooltip} </x-text> `)}>
          </x-tooltip>
        </x-icon-button>
        <x-icon-button
          mode="icon"
          icon=${this.configuration.settings.icon}
          @click=${() => this.dispatchEvent(new CustomEvent(Event.OPEN_SETTINGS, { composed: true }))}
        >
          <x-tooltip .text=${xComponents(html` <x-text> ${this.configuration.settings.tooltip} </x-text> `)}>
          </x-tooltip>
        </x-icon-button>
        ${repeat(
      this._consumedOptions.value ?? [],
      (option) => html`
            <x-icon-button
              mode="icon"
              icon=${option.icon}
              @click=${() => option.action?.(this._consumdData.value ?? [])}
            >
              ${option.tooltip ? html` <x-tooltip .text=${option.tooltip()}> </x-tooltip> ` : nothing}
            </x-icon-button>
          `
    )}
      </x-flex>
    `;
  }
  _sizePicker() {
    return html`
      <x-buildify-size-picker
        @value-picked=${(e) => {
      this.dispatchEvent(new CustomEvent("size-changed", { detail: { value: e.detail.value } }));
    }}
      >
      </x-buildify-size-picker>
    `;
  }
  get configuration() {
    return {
      preview: {
        icon: this._consumedMode.value === "edit" ? "visibility" : "edit",
        tooltip: this._consumedMode.value === "edit" ? "Zur Preview wechseln" : "Zum Editieren wechseln"
      },
      settings: {
        icon: "settings",
        tooltip: "Globale Einstellungen \xF6ffnen"
      },
      switchMinimize: {
        icon: this._isMinimized ? "keyboard_arrow_down" : "keyboard_arrow_up"
      },
      groups: {
        folderClosedIcon: "folder",
        folderOpenIcon: "folder_open"
      }
    };
  }
  dragDropWrapper(content) {
    return html`
      <x-drag-drop
        ${css({ width: "100%" })}
        allow-from=${`.bar-item`}
        child-selector=${`.bar-item`}
        duplicate
        .autoScrollContainer=${() => this._consumedAutoScrollContainer.value}
        @dragging-changed=${(e) => ThrowEventDraggingChanged(this, e.target.dragging)}
      >
        <x-grid
          space="xs"
          auto-fill-columns="150px"
          ${css({ width: "100%", display: this._isMinimized ? "flex" : "grid", flexWrap: "wrap" })}
        >
          ${content}
        </x-grid>
      </x-drag-drop>
    `;
  }
  styles() {
    return [
      ...super.styles(),
      {
        ":host": {
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          gap: this.spacing.md,
          background: this._consumedMode.value === "edit" ? `linear-gradient(272deg, ${this.colors.tone.warning} 200px, ${this.colors.light} 350px, ${this.colors.light})` : this.colors.light,
          color: this._consumedMode.value === "edit" ? this.colors.on.tone.warning : this.colors.on.light,
          padding: `${this.spacing.sm} ${this.spacing.md}`,
          position: "sticky",
          top: "0",
          zIndex: "2"
        }
      }
    ];
  }
};
__decorateClass([
  state()
], XBuildifyToolbar.prototype, "_selectedGroup", 2);
__decorateClass([
  state()
], XBuildifyToolbar.prototype, "_isMinimized", 2);
XBuildifyToolbar = __decorateClass([
  customElement("x-buildify-toolbar")
], XBuildifyToolbar);
export {
  XBuildifyToolbar
};
